import { Grid, Typography, List, ListItem, ListItemText, ListItemIcon } from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DATE_FORMATS } from '@callmart/types';
import { RecordCreatedAt } from '../../enums/RecordCreatedAt';
import {
    useLastLocationDeltaUploadDate,
    useTotalBookedBookingsByDateRangeAndWorktypeAndLocation,
    useTotalForecastDeltasByDateRangeAndWorktypeAndLocation,
    useAllBookedBookingsByDateRangeWorktypeAndLocation,
    useAllForecastDeltasByDateRangeWorktypeAndLocation,
    useTotalBookedBookingsByDateAndLocationGroupByWorktype,
} from '../../hooks';
import { createTableRows, combineChartDataInOneArray } from '../../utils/agGridUtils';
import { useLastCapacityReportUploadDatePerLocation } from '../../hooks/api/capacityReports';
import DashboardTotals from './DashboardTotals';
import DashboardChart from './DashboardChart';
import DashboardTable from './DashboardTable';
import { createMapWithTableRows, combineLatestAndOldestBookings, createColumnDefs } from './helpers/utils';
import { formatSlotTime } from '@utils/dayjs';

function DashboardOSP({ dateFrom, dateTo, worktype, worktypes, loggedInUser, showSlotIntervalOnChart }) {
    const { t } = useTranslation('dashboard');
    const location = useMemo(() => loggedInUser?.primaryLocation?.id, [loggedInUser]);
    const loggedInUserLocations = useMemo(
        () => (loggedInUser?.id ? [loggedInUser.primaryLocation, ...loggedInUser.additionalLocations] : []),
        [loggedInUser]
    );
    const loggedInUserLocationsIds = useMemo(
        () => loggedInUserLocations.map((location) => location.id),
        [loggedInUserLocations]
    );

    // Last upload dates
    const latestLocationDeltaUploadDate = useLastLocationDeltaUploadDate(loggedInUser?.primaryLocation?.id);
    const [latestCapacityReportUploadDatesPerLocation] =
        useLastCapacityReportUploadDatePerLocation(loggedInUserLocationsIds);
    const latestCapacityReportUploadDates = useMemo(
        () =>
            latestCapacityReportUploadDatesPerLocation
                .map((capacityReport) => {
                    const location = loggedInUserLocations.find((loc) => loc.id === capacityReport.locationId);
                    return {
                        city: location?.city,
                        latestUpload: capacityReport.value
                            ? formatSlotTime(capacityReport.value, `${DATE_FORMATS.DATE} ${DATE_FORMATS.TIME}`)
                            : '',
                    };
                })
                .filter((location) => location.city),
        [latestCapacityReportUploadDatesPerLocation, loggedInUserLocations]
    );

    // Totals
    const totalForecastDeltas = useTotalForecastDeltasByDateRangeAndWorktypeAndLocation({
        dateFrom,
        dateTo,
        worktype,
        location,
    });
    const totalBookedBookings = useTotalBookedBookingsByDateRangeAndWorktypeAndLocation({
        dateFrom,
        dateTo,
        worktype,
        location,
        recordCreatedAt: RecordCreatedAt.LATEST,
    });
    const totalAvailableBookings = useMemo(
        () => ({
            positive: totalForecastDeltas.positive - totalBookedBookings.positive,
            negative: totalForecastDeltas.negative - totalBookedBookings.negative,
        }),
        [totalForecastDeltas, totalBookedBookings]
    );
    const totals = useMemo(
        () => ({
            totalForecastDeltas,
            totalBookedBookings,
            totalAvailableBookings,
        }),
        [totalForecastDeltas, totalBookedBookings, totalAvailableBookings]
    );

    // Chart data
    const [bookedCalls] = useAllBookedBookingsByDateRangeWorktypeAndLocation({ dateFrom, dateTo, worktype, location });
    const [forecastDeltaCalls] = useAllForecastDeltasByDateRangeWorktypeAndLocation({
        dateFrom,
        dateTo,
        worktype,
        location,
    });
    const chartData = useMemo(
        () => combineChartDataInOneArray(bookedCalls, forecastDeltaCalls),
        [bookedCalls, forecastDeltaCalls]
    );

    // Column defs
    const columnDefs = useMemo(
        () =>
            createColumnDefs(
                worktypes,
                {
                    fieldPrefix: 'worktypeId',
                    idField: 'id',
                    getHeaderName: (worktype) => worktype.worktype,
                },
                t
            ),
        [worktypes, t]
    );

    // Table data
    const [latestBookedBookings] = useTotalBookedBookingsByDateAndLocationGroupByWorktype({
        dateFrom,
        dateTo,
        location,
        recordCreatedAt: RecordCreatedAt.LATEST,
    });
    const [oldestBookedBookings] = useTotalBookedBookingsByDateAndLocationGroupByWorktype({
        dateFrom,
        dateTo,
        location,
        recordCreatedAt: RecordCreatedAt.OLDEST,
    });

    const bookings = useMemo(
        () => combineLatestAndOldestBookings(latestBookedBookings, oldestBookedBookings, 'worktypeId'),
        [latestBookedBookings, oldestBookedBookings]
    );
    const tableRows = useMemo(
        () => createTableRows(createMapWithTableRows(bookings, 'worktypeId'), 'bookingType'),
        [bookings]
    );

    return (
        <>
            <Grid item xs={12} sm={12} md={7} lg={3}>
                <DashboardTotals totals={totals}></DashboardTotals>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={5}>
                <DashboardChart chartData={chartData} showSlotInterval={showSlotIntervalOnChart}></DashboardChart>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} sx={{ mt: 2, mb: 2 }}>
                <DashboardTable tableRows={tableRows} columnDefs={columnDefs}></DashboardTable>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12}>
                <Typography>
                    {t('osp.lastDeltaReport')}: <span className="notranslate">{latestLocationDeltaUploadDate}</span>
                </Typography>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12}>
                <Typography>{t('osp.lastPotentialReport')}: </Typography>
                <List>
                    {latestCapacityReportUploadDates.map((latesData) => (
                        <ListItem key={latesData.city}>
                            <ListItemIcon>
                                <CircleIcon style={{ color: 'var(--primary)' }} />
                            </ListItemIcon>
                            <ListItemText primary={`${latesData.city}: ${latesData.latestUpload}`} />
                        </ListItem>
                    ))}
                </List>
            </Grid>
        </>
    );
}

export default DashboardOSP;
